import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import isRequiredIf from 'react-proptype-conditional-require';
import PageContainer from '@deliveroo/consumer-component-library/components/PageContainer';
import Button from '@deliveroo/consumer-component-library/components/Button';
import Heading from '@deliveroo/consumer-component-library/components/Heading';
import P from '@deliveroo/consumer-component-library/components/P';

import ResponsiveGridLayout from '../ResponsiveGridLayout';
import BrandMenuItem from '../BrandMenuItem';
import styles from './styles.scss';

/**
 * A list of top menu items for a given brand.
 */
export default function BrandTopPicks({
  heading,
  description,
  menuItems,
  menuLink,
  viewMenuButtonLabel,
  onViewMenuButtonClick,
  children,
}) {
  return (
    <PageContainer fullWidth="mobile-only">
      <Heading level={2} size="medium" spacing={{ mobile: '0 0 x-small 0', tablet: '0 0 small 0' }}>
        <span className={styles.heading}>{heading}</span>
      </Heading>
      <div className={styles.description}>
        <P color="grey" spacing={{ mobile: '0 0 medium 0', tablet: '0 0 x-large 0' }}>
          {description}
        </P>
      </div>
      <div className={classnames({ [styles.noButton]: !viewMenuButtonLabel })}>
        <ResponsiveGridLayout
          gutter="medium"
          maxColumns={3}
          minColumnWidth={260}
          hasMobileGutter={false}
        >
          {React.Children.map(children, (child) => (
            <div className={styles.menuItem} key={child.props.name}>
              {child}
            </div>
          ))}

          {!children &&
            menuItems.map(({ name, description: itemDescription, imageUrl }) => (
              <div className={styles.menuItem} key={name}>
                <BrandMenuItem name={name} description={itemDescription} imageUrl={imageUrl} />
              </div>
            ))}
        </ResponsiveGridLayout>
      </div>
      {viewMenuButtonLabel && (
        <div className={styles.viewMenuButton}>
          <Button
            type="secondary"
            href={menuLink}
            label={viewMenuButtonLabel}
            onClick={onViewMenuButtonClick}
          >
            {viewMenuButtonLabel}
          </Button>
        </div>
      )}
    </PageContainer>
  );
}

BrandTopPicks.propTypes = {
  /**
   * A heading for the selection of top picks
   */
  heading: PropTypes.string.isRequired,

  /**
   * A description for the top picks
   */
  description: PropTypes.string.isRequired,

  /**
   * An array of menu items to display. Only required if child nodes aren’t supplied.
   */
  menuItems: isRequiredIf(
    PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        imageUrl: PropTypes.string,
      }),
      (props) => !props.children,
    ),
  ),

  /**
   * An optional menu link to link through to a given restaurant menu
   */
  menuLink: PropTypes.string,

  /**
   * The label for the view menu button, if displayed
   */
  viewMenuButtonLabel: PropTypes.string,

  /**
   * The optional click handler for the view menu button, if one was provided
   */
  onViewMenuButtonClick: PropTypes.func,

  /**
   * A set of children to render, in place of the list of menu items, to allow
   * landing pages to explicitly define the set of child menu items rendered.
   */
  children: PropTypes.node,
};

BrandTopPicks.defaultProps = {
  menuLink: undefined,
  viewMenuButtonLabel: undefined,
  onViewMenuButtonClick: undefined,
  menuItems: undefined,
  children: undefined,
};
